// File hashing before an upload: files up to 128 MiB are hashed with WebCrypto in a Web Worker (the main thread stays responsive), larger files are not pre-hashed (the server de-duplicates them at completion); without Worker support the hash runs inline.
import { sha256, toHex } from "./parts";

export const PREHASH_MAX_BYTES = 128 * 1024 * 1024;

interface WorkerReply {
  sha256?: string;
  error?: string;
}

export function canPrehash(file: Pick<File, "size">): boolean {
  return file.size > 0 && file.size <= PREHASH_MAX_BYTES;
}

export async function hashFile(file: File, signal?: AbortSignal): Promise<string | null> {
  if (!canPrehash(file)) {
    return null;
  }
  if (typeof Worker === "undefined") {
    return toHex(await sha256(await file.arrayBuffer()));
  }
  const worker = new Worker(new URL("./hash.worker.ts", import.meta.url), { type: "module" });
  try {
    return await new Promise<string>((resolve, reject) => {
      signal?.addEventListener("abort", () => {
        reject(new DOMException("Hashing was cancelled.", "AbortError"));
      });
      worker.addEventListener("message", (event: MessageEvent<WorkerReply>) => {
        if (event.data.sha256 !== undefined) {
          resolve(event.data.sha256);
        } else {
          reject(new Error(event.data.error ?? "The file could not be hashed."));
        }
      });
      worker.addEventListener("error", () => {
        reject(new Error("The file could not be hashed."));
      });
      worker.postMessage(file);
    });
  } finally {
    worker.terminate();
  }
}
